import _ from "lodash";
import { String } from "typescript-string-operations";
import enResources from "../localization/resource_en.json";
import { appStore } from "../modules/store";

export class LocalizationService {
  locale: string;
  resources: any;
  private unsubscribe: (() => void) | null = null;

  constructor() {
    // Initialize with default locale
    this.locale = "en";
    this.resources = enResources;

    // Subscribe to store changes to update locale when account loads
    this.unsubscribe = appStore.subscribe(() => {
      this.updateLocaleFromStore();
    });

    // Try to set initial locale from store
    this.updateLocaleFromStore();
  }

  private updateLocaleFromStore() {
    const state = appStore.getState();

    // Only update if account is loaded and has a language preference
    if (!state.account?.isLoading && state.account?.language) {
      const newLocale = state.account.language;

      // Only update if locale changed
      if (newLocale !== this.locale) {
        this.locale = newLocale;
        this.updateResources();
      }
    }
  }

  private updateResources() {
    if (this.locale === "en") {
      this.resources = enResources;
    }
    // Add more locale conditions here as you add more language files
    // else if (this.locale === "fr") {
    //   this.resources = frResources;
    // }
  }

  // Cleanup method if needed
  destroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  getLocalizationStrings() {
    return this.resources;
  }

  // Examples:
  // localizationService.getLocalizedString('errors.failed')
  // localizationService.getLocalizedString('errors.failedPattern', 'domainObjects.extractor');
  // localizationService.getLocalizedString('errors.failedPattern', '2013-01-02');
  getLocalizedString(jsonPath: string, ...argumentJsonPaths: string[]): string {
    return this.getLocalizedStringAndLog(jsonPath, argumentJsonPaths, true);
    // return this.getLocalizedStringAndLog("test." + jsonPath, argumentJsonPaths, true);
  }

  formatValue(value: any): any {
    if (typeof value === "boolean") {
      return value === true
        ? localizationService.getLocalizedString("constants.yes")
        : localizationService.getLocalizedString("constants.no");
    }
    return value;
  }

  private getLocalizedStringAndLog(
    jsonPath: string,
    argumentJsonPaths: string[],
    logAsWarning: boolean
  ): string {
    if (!jsonPath) {
      return "";
    }
    // For testing: if language is not English, return "x" to identify untranslated strings
    if (this.locale !== "en") {
      const localizedText = _.get(enResources, jsonPath);
      if (!localizedText) {
        return "NO STRING";
      }
      return "x";
    }

    const localizedText = _.get(this.resources, jsonPath);
    if (localizedText) {
      if (argumentJsonPaths.length > 0) {
        const localizedArguments = argumentJsonPaths.map((x) => {
          return this.getLocalizedStringAndLog(x, [], false);
        });
        return String.format(localizedText, ...localizedArguments);
      }
      return localizedText;
    }
    if (logAsWarning) {
      console.warn(`translation for ${jsonPath} is missing.`);
    }
    return jsonPath;
  }
}

export const localizationService = new LocalizationService();
